import supabase from "./supabase";
import { compressImage } from "./imageCompress";

export const insertRow = async (table: string, payload: Record<string, any>) => {
  const { error } = await supabase().from(table).insert(payload);
  if (error) throw error;
};

export const uploadFile = async (
  bucket: string,
  prefix: string,
  file: File
): Promise<{ path: string; url: string }> => {
  // The quote form accepts jpg/png from the public, so compress images here too.
  // PDFs, Office docs and zips are returned untouched by compressImage.
  const prepared = await compressImage(file);
  const safe = prepared.name.replace(/[^A-Za-z0-9._-]/g, "_");
  const path = `${prefix}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${safe}`;
  const { error } = await supabase().storage.from(bucket).upload(path, prepared, {
    cacheControl: "3600",
    upsert: false,
    contentType: prepared.type || undefined,
  });
  if (error) throw error;
  const { data } = supabase().storage.from(bucket).getPublicUrl(path);
  return { path, url: data.publicUrl };
};

// ─── Email notifications via backend /api/notify (Resend) ─────────────────
// Resend blocks direct browser calls (CORS), so we proxy through FastAPI.
// Failures NEVER block the form submission. Errors are logged to console only.
const NOTIFY_URL = `${process.env.REACT_APP_BACKEND_URL || ""}/api/notify`;
const DEFAULT_NOTIFY_TO = "admin.dubai@ideationav.com";

const escapeHtml = (s: any): string => String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));

const rowsToHtml = (rows: Array<[string, any]>): string =>
  rows
    .filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== "")
    .map(([k, v]) => `<tr><td style="padding:6px 12px 6px 0;color:#666;font-family:Helvetica,Arial,sans-serif;font-size:13px;white-space:nowrap;vertical-align:top">${escapeHtml(k)}</td><td style="padding:6px 0;color:#000;font-family:Helvetica,Arial,sans-serif;font-size:13px;line-height:1.55;white-space:pre-wrap">${escapeHtml(v)}</td></tr>`)
    .join("");

const wrapHtml = (title: string, bodyHtml: string): string => `
<div style="background:#f5f5f5;padding:24px;font-family:Helvetica,Arial,sans-serif">
  <div style="max-width:560px;margin:0 auto;background:#fff;border:1px solid #e5e5e5">
    <div style="background:#06090E;padding:18px 22px;color:#fff;font-family:Helvetica,Arial,sans-serif">
      <div style="font-size:11px;letter-spacing:0.22em;color:#00AAFF">IDEATION AV · NOTIFICATIONS</div>
      <div style="font-size:18px;margin-top:6px">${escapeHtml(title)}</div>
    </div>
    <div style="padding:18px 22px"><table style="border-collapse:collapse;width:100%">${bodyHtml}</table></div>
    <div style="padding:14px 22px;border-top:1px solid #eee;color:#888;font-size:11px;font-family:Helvetica,Arial,sans-serif">Sent automatically from ideationav.com</div>
  </div>
</div>`;

export const sendNotificationEmail = async (to: string | undefined, subject: string, html: string): Promise<void> => {
  const recipient = to && to.trim() ? to.trim() : DEFAULT_NOTIFY_TO;
  if (!NOTIFY_URL || NOTIFY_URL.startsWith("/api")) {
    console.warn("[notify] REACT_APP_BACKEND_URL missing — cannot send email");
    return;
  }
  console.log("[notify] sending →", recipient, "subject:", subject);
  try {
    const resp = await fetch(NOTIFY_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ to: recipient, subject, html }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || data?.ok === false) {
      console.error("[notify] backend rejected:", resp.status, data);
    } else {
      console.log("[notify] sent OK", data);
    }
  } catch (e) {
    console.error("[notify] fetch exception:", e);
  }
};

export type NotifyKind = "quote" | "contact" | "freelancer" | "job";
interface NotifyArgs {
  kind: NotifyKind;
  to?: string;
  data: Record<string, any>;
  jobTitle?: string;
}
export const notifyForm = async ({ kind, to, data, jobTitle }: NotifyArgs): Promise<void> => {
  const name = [data.first_name, data.last_name].filter(Boolean).join(" ").trim() || data.name || "—";
  let subject = "";
  let rows: Array<[string, any]> = [];

  if (kind === "quote") {
    const eventLabel = data.event_type || data.company || name;
    subject = `New Quote Request — ${eventLabel}`;
    rows = [
      ["Name", name], ["Email", data.email], ["Phone", data.phone], ["Company", data.company],
      ["Event Type", data.event_type], ["Event Date", data.event_date], ["Venue", data.venue],
      ["Budget", data.budget],
      ["Services Selected", Array.isArray(data.services_required) ? data.services_required.join(", ") : data.services_required],
      ["Additional Details", data.additional_details],
      ["Attachments", Array.isArray(data.files) && data.files.length ? data.files.map((f: any) => `${f.name} (${f.url})`).join("\n") : ""],
    ];
  } else if (kind === "contact") {
    subject = `New Contact Message — ${name}`;
    rows = [
      ["Name", name], ["Email", data.email], ["Phone", data.phone],
      ["Subject", data.subject], ["Message", data.message],
    ];
  } else if (kind === "freelancer") {
    subject = `New Freelancer Application — ${name}`;
    rows = [
      ["Name", name], ["Email", data.email], ["Phone", data.phone],
      ["Specialisation", data.specialisation], ["Experience", data.experience],
      ["Portfolio", data.portfolio_url], ["Equipment & Skills", data.skills],
      ["CV", data.cv_url],
    ];
  } else if (kind === "job") {
    subject = `New Job Application — ${jobTitle || data.job_title || "—"} — ${name}`;
    rows = [
      ["Job", jobTitle || data.job_title], ["Name", name],
      ["Email", data.email], ["Phone", data.phone],
      ["Experience", data.experience], ["Portfolio", data.portfolio_url],
      ["Cover Note", data.cover_note], ["CV", data.cv_url],
    ];
  }
  const html = wrapHtml(subject, rowsToHtml(rows));
  await sendNotificationEmail(to, subject, html);
};
